import { Outlet, useLocation } from "react-router-dom";
import { useCallback, useEffect } from "react";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SkipLink } from "@/components/SkipLink";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { StickyCtaBar } from "@/components/StickyCtaBar";
import { FooterCta } from "@/components/FooterCta";
import { captureUtmParams } from "@/lib/utm";
import { useWaitlist } from "@/components/WaitlistContext";
import { useExitIntent } from "@/hooks/useExitIntent";
import { useScrollDepth } from "@/hooks/useScrollDepth";

/**
 * App shell. Renders the skip link, header, main landmark, and footer around
 * each route. Also scrolls the window to the top on route change — important
 * for screen readers that rely on focus reset to announce new pages.
 */
export function Layout() {
  const { pathname } = useLocation();
  const { openWaitlist } = useWaitlist();
  const handleExitIntent = useCallback(() => openWaitlist(), [openWaitlist]);

  useExitIntent(handleExitIntent);
  useScrollDepth();

  useEffect(() => {
    captureUtmParams();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Move focus to the main landmark so screen-reader users get a new-page cue.
    const main = document.getElementById("main-content");
    if (main) main.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <>
      <SkipLink />
      <Header />
      <main
        id="main-content"
        // tabIndex=-1 lets us programmatically focus the landmark on route change
        // without inserting it into the regular tab order.
        tabIndex={-1}
        className="focus:outline-none"
      >
        <Outlet />
      </main>
      {pathname !== "/404" && <FooterCta />}
      <Footer />
      <WaitlistDialog />
      <StickyCtaBar />
    </>
  );
}
